import React, { useState } from 'react';
import { PencilEditIcon } from '../../icons';
import PlusIcon from '../../icons/PlusIcon';
import AddSkillsModal from '../../modals/AddSkillsModal';
import './skills.css';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

import { SkillsProps } from '../../../types/profile';
import { PlaceholderLabel } from '../PlaceholderLabel';
import { profileSkillChipClassName } from '../../../pages/Onboarding/OnboardingUI';
import { skillChipHref } from '@/utils/seoSlug';
import { PROFILE_MODULE_CLASS } from '@/theme/surfaceClasses';

function Skills({
    selectedSkills = [],
    setSelectedSkills,
    saveSkills,
    canEdit = false,
}: SkillsProps) {
    const [showAddSkillsModal, setShowAddSkillsModal] = useState(false);
    const navigate = useNavigate();

    // Get the logged-in user's profile from Redux state
    const loggedInUserProfile = useSelector((state: RootState) => state.auth.profile);
    const isSignedIn = useSelector((state: RootState) => !!state.auth.user);

    // Function to check if a skill matches with logged-in user's skills
    const isMatchingSkill = (skillToCheck: { _id: string; name?: string; displayName?: string }) => {
        if (!loggedInUserProfile?.skills || loggedInUserProfile.skills.length === 0) return false;
        if (canEdit) return false; // Don't highlight skills on your own profile

        return loggedInUserProfile.skills.some(
            userSkill => userSkill._id === skillToCheck._id || userSkill.name === skillToCheck.name
        );
    };

    const addSkills = () => {
        setShowAddSkillsModal(true);
    };

    const editSkills = () => {
        setShowAddSkillsModal(true);
    };

    const onSaveSkills = async () => {
        // First save the skills
        try {
            await saveSkills();
        } catch (error) {
            console.error('Error saving skills:', error);
        }
        // Then close the modal
        setShowAddSkillsModal(false);
    };

    const formatSkillName = (name: string | undefined) => {
        if (!name) return ''; // Return an empty string if name is undefined
        return name
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const skillsSection =
        selectedSkills?.length === 0 && canEdit ? (
            <div className="flex items-center justify-center bg-blue-200 rounded-lg h-48 w-full">
                <button
                    className="group flex justify-center items-center bg-blue-200 rounded-lg pt-2 pb-2 pl-2 flex-col flex-grow h-full w-full transition-colors hover:bg-blue-300"
                    aria-label="Add skills. Get found for what you do"
                    style={{
                        paddingRight: '2px',
                        position: 'relative',
                        overflow: 'hidden',
                        minHeight: '150px',
                        maxHeight: 'none'
                    }}
                    onClick={addSkills}
                >
                    <PlusIcon
                        width={23}
                        height={33}
                    />
                    <PlaceholderLabel className="text-sm" title="Add skills" benefit="Get found for what you do" />
                </button>
            </div>
        ) : (
            <div className="flex flex-wrap gap-2">
                {selectedSkills
                    ?.slice() // Create a copy to avoid mutating the original array
                    .sort((a, b) => {
                        const aMatches = isMatchingSkill(a);
                        const bMatches = isMatchingSkill(b);
                        // Sort matching skills first
                        if (aMatches && !bMatches) return -1;
                        if (!aMatches && bMatches) return 1;
                        return 0;
                    })
                    .map((skill) => {
                        const label = formatSkillName(skill.displayName || skill.name);
                        const isMatching = isMatchingSkill(skill);
                        return (
                            <button
                                type="button"
                                key={skill._id}
                                className={profileSkillChipClassName(!canEdit && isMatching)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigate(skillChipHref(skill.displayName || skill.name, isSignedIn));
                                }}
                            >
                                {label}
                            </button>
                        );
                    })}
            </div>
        );

    return ((!selectedSkills || selectedSkills.length === 0) && canEdit) ||
        (selectedSkills && selectedSkills.length > 0) ? (
            <div className={`${PROFILE_MODULE_CLASS} flex flex-col p-4`}>
                <AddSkillsModal
                    show={showAddSkillsModal}
                    setShow={setShowAddSkillsModal}
                    onClose={() => setShowAddSkillsModal(false)}
                    selectedSkills={selectedSkills}
                    setSelectedSkills={setSelectedSkills}
                    saveSkills={onSaveSkills}
                />
                <div className='flex flex-row justify-between items-center mb-2'>
                    <h2 className="text-lg font-bold text-eerieblack">Skills</h2>
                    {canEdit && (
                        <button
                            className="bg-white p-[6px] rounded-full border-gray-400 border-[1px] cursor-pointer"
                            onClick={editSkills}
                        >
                            <PencilEditIcon
                                width={24}
                                height={24}
                                fill={'#626262'}
                                fillOpacity={0.8}
                            />
                        </button>
                    )}
                </div>
                {skillsSection}
            </div>
        ) : (
            <></>
        );
}

export default Skills;
